/**
 * UnderwritingSection - the Ausschreibungs-Screen Tab 5b body (Underwriting / Versicherer-Rueckmeldung,
 * Prozessschritte 19-20; P2 Placement Slice 14). The broker records the carrier response from the
 * claimed reply, runs the E6 condition rounds + the E7 alternative/fallback, and records the Deckung /
 * Police. The server owns every gate; the UI triggers + renders.
 */

import { useEffect, useState } from 'react';
import { EntityLink } from '../../components/ui/EntityLink';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { dateShortDe, dateTimeShortDe } from '../../lib/format';
import { ApiError } from '../../lib/api-client';
import {
  ABGELEHNT_QUELLE_LABELS,
  FALLBACK_ART_LABELS,
  UW_ABGELEHNT_HINWEIS,
  UW_AKZEPTANZ_MELDEN_HINWEIS,
  UW_ALTERNATIVE_AKZEPTIERT_HINWEIS,
  UW_DECKUNG_HINWEIS,
  UW_KEINE_ALTERNATIVEN_HINWEIS,
  UW_KEINE_RUECKMELDUNG_HINWEIS,
  UW_NOCH_NICHT_EINGEREICHT_HINWEIS,
  UW_POLICE_NICHT_GEFUNDEN_HINWEIS,
  UW_POLICE_VERKNUEPFT_HINWEIS,
  UW_RUECKMELDUNG_HINWEIS,
  UW_RUNDE_UEBERFAELLIG_HINWEIS,
  type UnderwritingResponse,
  type UwRundeEintrag,
} from '@brokly/shared';
import { UnderwritingStatusBadge } from './UnderwritingStatusBadge';
import { UwEntscheidDialog } from './UwEntscheidDialog';
import { UwBedingungenDialog } from './UwBedingungenDialog';
import { UwRundeEntscheidDialog } from './UwRundeEntscheidDialog';
import { UwAlternativeDialog } from './UwAlternativeDialog';
import { UwFallbackDialog } from './UwFallbackDialog';
import { UwDeckungDialog } from './UwDeckungDialog';
import { UwPoliceErhaltenDialog } from './UwPoliceErhaltenDialog';
import {
  usePlacementUnderwriting,
  useAnnehmenUw,
  useAblehnenUw,
  useBedingungenUw,
  useRundeEntscheidUw,
  useAlternativeUw,
  useFallbackUw,
  useDeckungUw,
  usePoliceErhaltenUw,
} from './usePlacementUnderwriting';

type DialogKind = 'annehmen' | 'ablehnen' | 'bedingungen' | 'runde' | 'alternative' | 'fallback' | 'deckung' | 'police' | null;

function errCode(err: unknown): string {
  return err instanceof ApiError && err.code ? err.code : 'Aktion fehlgeschlagen';
}

function DeltaTable({ runde }: { runde: UwRundeEintrag }) {
  return (
    <table className="mt-2 w-full text-sm" data-testid="uw-delta-table">
      <thead>
        <tr className="text-left text-muted-foreground"><th>Feld</th><th>Bisher</th><th>Neu</th></tr>
      </thead>
      <tbody>
        {runde.delta.map((d) => {
          const changed = runde.geaenderte_felder.includes(d.feld);
          return (
            <tr key={d.feld} className={changed ? 'font-medium text-foreground' : 'text-muted-foreground'} data-testid={changed ? 'uw-delta-changed' : undefined}>
              <td>{d.label}</td>
              <td>{d.alt ?? '-'}</td>
              <td>{d.neu ?? '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function UnderwritingSection({
  placementId,
  canWrite,
  openKind,
  onOpenHandled,
  onAntragVorbereiten,
}: {
  placementId: string;
  canWrite: boolean;
  openKind?: 'annehmen' | 'ablehnen' | null;
  onOpenHandled?: () => void;
  onAntragVorbereiten?: () => void;
}) {
  const uw = usePlacementUnderwriting(placementId);
  const annehmen = useAnnehmenUw(placementId);
  const ablehnen = useAblehnenUw(placementId);
  const bedingungen = useBedingungenUw(placementId);
  const rundeEntscheid = useRundeEntscheidUw(placementId);
  const alternative = useAlternativeUw(placementId);
  const fallback = useFallbackUw(placementId);
  const deckung = useDeckungUw(placementId);
  const police = usePoliceErhaltenUw(placementId);

  const [dialog, setDialog] = useState<DialogKind>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (openKind) {
      setDialog(openKind);
      setError(null);
      onOpenHandled?.();
    }
  }, [openKind, onOpenHandled]);

  const data = uw.data;
  if (!data) return <section data-testid="placement-underwriting-section" id="underwriting"><p className="text-sm text-muted-foreground">Lade …</p></section>;

  const a = data.antrag;
  const run = (p: Promise<unknown>) => {
    setError(null);
    p.then(() => setDialog(null)).catch((err) => setError(errCode(err)));
  };

  const akz = data.aktionen;
  const offeneRunde = data.runden.find((r) => r.id === data.offene_runde_id) ?? null;
  const akzBedingung = data.runden.find((r) => r.art === 'bedingungen_geaendert' && r.status === 'akzeptiert');

  return (
    <section className="flex flex-col gap-4 rounded-md border border-border p-4" data-testid="placement-underwriting-section" id="underwriting">
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Underwriting</h2>
        <UnderwritingStatusBadge status={a?.status ?? null} />
      </header>

      {a === null ? (
        <p className="text-sm text-muted-foreground" data-testid="uw-noch-nicht">{UW_NOCH_NICHT_EINGEREICHT_HINWEIS}</p>
      ) : (
        <>
          {canWrite && (
            <div className="flex flex-wrap gap-2" data-testid="uw-actions">
              {akz.annehmen && <Button size="sm" onClick={() => { setDialog('annehmen'); setError(null); }} data-testid="uw-action-annehmen">Annahme erfassen</Button>}
              {akz.ablehnen && <Button size="sm" variant="destructive" onClick={() => { setDialog('ablehnen'); setError(null); }} data-testid="uw-action-ablehnen">Ablehnung erfassen</Button>}
              {akz.bedingungen_erfassen && <Button size="sm" variant="outline" onClick={() => { setDialog('bedingungen'); setError(null); }} data-testid="uw-action-bedingungen">Bedingungen geaendert</Button>}
              {akz.runde_entscheid_erfassen && <Button size="sm" onClick={() => { setDialog('runde'); setError(null); }} data-testid="uw-action-runde">Kundenentscheid erfassen</Button>}
              {akz.alternative_vorschlagen && <Button size="sm" variant="outline" onClick={() => { setDialog('alternative'); setError(null); }} data-testid="uw-action-alternative">Alternative vorschlagen</Button>}
              {akz.fallback_erfassen && <Button size="sm" variant="outline" onClick={() => { setDialog('fallback'); setError(null); }} data-testid="uw-action-fallback">Weiteres Vorgehen</Button>}
              {akz.abbrechen && !akz.fallback_erfassen && <Button size="sm" variant="outline" onClick={() => { setDialog('fallback'); setError(null); }} data-testid="uw-action-abbrechen">Ausschreibung abbrechen</Button>}
              {akz.deckung_bestaetigen && <Button size="sm" onClick={() => { setDialog('deckung'); setError(null); }} data-testid="uw-action-deckung">Deckung bestaetigen</Button>}
              {akz.police_erhalten && <Button size="sm" onClick={() => { setDialog('police'); setError(null); }} data-testid="uw-action-police">Police erhalten</Button>}
              {akz.antrag_vorbereiten && <Button size="sm" variant="outline" onClick={() => onAntragVorbereiten?.()} data-testid="uw-action-vorbereiten">Antrag vorbereiten</Button>}
            </div>
          )}

          {/* Rueckmeldung */}
          {data.carrier_reply && (
            <div className="rounded-md border border-border p-3 text-sm" data-testid="uw-rueckmeldung-card">
              <p>Rueckmeldung eingegangen am {dateTimeShortDe(data.carrier_reply.received_at)}</p>
              <p className="mt-1 flex gap-3 text-xs">
                <a href={`/posteingang/${data.carrier_reply.communication_id}`} className="underline" data-testid="uw-rueckmeldung-mail">Mail oeffnen</a>
                {data.carrier_reply.task_id && <a href={`/aufgaben/${data.carrier_reply.task_id}`} className="underline" data-testid="uw-rueckmeldung-task">Aufgabe oeffnen</a>}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{data.carrier_reply.task_open ? UW_RUECKMELDUNG_HINWEIS : UW_KEINE_RUECKMELDUNG_HINWEIS}</p>
            </div>
          )}
          {!data.carrier_reply && a.status === 'eingereicht' && (
            <p className="text-xs text-muted-foreground" data-testid="uw-keine-rueckmeldung">{UW_KEINE_RUECKMELDUNG_HINWEIS}</p>
          )}

          {/* Entscheid */}
          {data.entscheid && (
            <div className="rounded-md border border-border p-3 text-sm" data-testid="uw-entscheid-card">
              <p>
                {data.entscheid.art === 'angenommen' ? 'Angenommen' : 'Abgelehnt'} am {dateTimeShortDe(data.entscheid.at)}
                {data.entscheid.by_name ? ` von ${data.entscheid.by_name}` : ''}
                {data.entscheid.implizit && <Badge variant="neutral" className="ml-2">implizit</Badge>}
              </p>
              {data.entscheid.abgelehnt_quelle_label && <p className="text-xs text-muted-foreground">{ABGELEHNT_QUELLE_LABELS[data.entscheid.abgelehnt_quelle!]}</p>}
              {data.entscheid.grund && <p className="mt-1 text-xs">{data.entscheid.grund}</p>}
            </div>
          )}

          {/* Rounds */}
          {data.runden.length > 0 && (
            <div className="flex flex-col gap-2" data-testid="uw-runden-list">
              {data.runden.map((r) => (
                <div key={r.id} className="rounded-md border border-border p-3 text-sm" data-testid="uw-runde-card">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{r.art_label}{r.alternative ? ` - ${r.alternative.carrier_name ?? r.alternative.carrier_uid} (Rang ${r.alternative.rang})` : ''}</span>
                    <Badge variant={r.status === 'akzeptiert' ? 'success' : r.status === 'abgelehnt' ? 'danger' : r.status === 'hinfaellig' ? 'neutral' : 'warning'}>{r.status_label}</Badge>
                  </div>
                  <DeltaTable runde={r} />
                  {r.ausschluesse_neu.length > 0 && <p className="mt-1 text-xs">Neue Ausschluesse: {r.ausschluesse_neu.join(', ')}</p>}
                  <p className="mt-1 text-xs text-muted-foreground">{r.bedingungen_text}</p>
                  <p className="mt-1 text-xs text-muted-foreground">via {r.zustellung.channel_label} am {dateTimeShortDe(r.zustellung.zugestellt_at)}, Frist {dateShortDe(r.zustellung.frist_date)}</p>
                  {r.zustellung.ueberfaellig && (
                    <p className="mt-1 text-xs text-status-danger" data-testid="uw-runde-ueberfaellig">
                      {UW_RUNDE_UEBERFAELLIG_HINWEIS}
                      {r.zustellung.task_id && <a href={`/aufgaben/${r.zustellung.task_id}`} className="ml-2 underline">Aufgabe</a>}
                    </p>
                  )}
                  {r.entscheid && <p className="mt-1 text-xs">Kunde: {r.entscheid.entscheid === 'akzeptiert' ? 'akzeptiert' : 'abgelehnt'} am {dateTimeShortDe(r.entscheid.entschieden_at)} ({r.entscheid.entscheider_name}, {r.entscheid.response_channel_label})</p>}
                </div>
              ))}
            </div>
          )}

          {/* Akzeptanz melden hint */}
          {akzBedingung && a.status === 'eingereicht' && (
            <p className="text-xs text-status-info" data-testid="uw-akzeptanz-melden">
              {UW_AKZEPTANZ_MELDEN_HINWEIS}
              {data.carrier_reply && <a href={`/posteingang/${data.carrier_reply.communication_id}`} className="ml-2 underline">Mail oeffnen</a>}
            </p>
          )}

          {/* Fallback / E7 */}
          {a.status === 'abgelehnt' && (
            <div className="rounded-md border border-border p-3 text-sm" data-testid="uw-fallback-card">
              <p className="text-xs text-muted-foreground">{UW_ABGELEHNT_HINWEIS}</p>
              {data.e7_task_id && <a href={`/aufgaben/${data.e7_task_id}`} className="text-xs underline" data-testid="uw-e7-task">Aufgabe oeffnen</a>}
              {data.alternativen.length === 0 && !data.akzeptierte_alternative_runde_id && <p className="mt-1 text-xs">{UW_KEINE_ALTERNATIVEN_HINWEIS}</p>}
              {data.fallback && <p className="mt-1 text-xs">Erfasst: {FALLBACK_ART_LABELS[data.fallback.art]}{data.fallback.grund ? ` - ${data.fallback.grund}` : ''}</p>}
              {data.akzeptierte_alternative_runde_id && <p className="mt-1 text-xs text-status-info" data-testid="uw-alternative-akzeptiert">{UW_ALTERNATIVE_AKZEPTIERT_HINWEIS}</p>}
            </div>
          )}

          {/* Deckung */}
          {data.deckung && (
            <div className="rounded-md border border-border p-3 text-sm" data-testid="uw-deckung-card">
              <p>Deckung bestaetigt am {dateTimeShortDe(data.deckung.at)}{data.deckung.by_name ? ` von ${data.deckung.by_name}` : ''}</p>
              {data.deckung.bemerkung && <p className="text-xs text-muted-foreground">{data.deckung.bemerkung}</p>}
              {a.status === 'deckungsbestaetigt' && <p className="text-xs text-muted-foreground">{UW_DECKUNG_HINWEIS}</p>}
            </div>
          )}

          {/* Police */}
          {data.police && (
            <div className="rounded-md border border-border p-3 text-sm" data-testid="uw-police-card">
              <p>Police erhalten am {dateTimeShortDe(data.police.at)} - Policennummer {data.police.policy_number}</p>
              {data.police.policy_id ? (
                <p className="mt-1 text-xs text-status-success" data-testid="uw-police-verknuepft">
                  <EntityLink onActivate={() => { window.location.href = `/policen/${data.police!.policy_id}`; }}>{data.police.policy_bezeichnung ?? data.police.policy_number}</EntityLink> - {UW_POLICE_VERKNUEPFT_HINWEIS}
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="uw-police-nicht-gefunden">{UW_POLICE_NICHT_GEFUNDEN_HINWEIS}</p>
              )}
            </div>
          )}

          {data.hinweise.map((h, i) => (
            <p key={i} role="note" className="text-xs text-muted-foreground">{h}</p>
          ))}
        </>
      )}

      {/* Dialogs */}
      {(dialog === 'annehmen' || dialog === 'ablehnen') && (
        <UwEntscheidDialog
          open
          onClose={() => setDialog(null)}
          art={dialog}
          facts={data}
          saving={annehmen.isPending || ablehnen.isPending}
          error={error}
          onSubmit={(body) => run((dialog === 'annehmen' ? annehmen : ablehnen).mutateAsync(body))}
        />
      )}
      <UwBedingungenDialog open={dialog === 'bedingungen'} onClose={() => setDialog(null)} facts={data} saving={bedingungen.isPending} error={error} onSubmit={(body) => run(bedingungen.mutateAsync(body))} />
      <UwRundeEntscheidDialog open={dialog === 'runde'} onClose={() => setDialog(null)} facts={data} saving={rundeEntscheid.isPending} error={error} onSubmit={(body) => offeneRunde && run(rundeEntscheid.mutateAsync({ rundeId: offeneRunde.id, body }))} />
      <UwAlternativeDialog open={dialog === 'alternative'} onClose={() => setDialog(null)} facts={data} saving={alternative.isPending} error={error} onSubmit={(body) => run(alternative.mutateAsync(body))} />
      <UwFallbackDialog open={dialog === 'fallback'} onClose={() => setDialog(null)} saving={fallback.isPending} error={error} onSubmit={(body) => run(fallback.mutateAsync(body))} />
      <UwDeckungDialog open={dialog === 'deckung'} onClose={() => setDialog(null)} facts={data} saving={deckung.isPending} error={error} onSubmit={(body) => run(deckung.mutateAsync(body))} />
      <UwPoliceErhaltenDialog open={dialog === 'police'} onClose={() => setDialog(null)} facts={data} saving={police.isPending} error={error} onSubmit={(body) => run(police.mutateAsync(body))} />
    </section>
  );
}

export type { UnderwritingResponse };
